/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { LegacyRef } from 'react';
import { ScorecardInnerLoaded, ScorecardResponse } from '../../types/scorecard';
import { SDGControlStrip } from '../sdg-control-strip';
import SdgDetailView from '../sdg/sdg-detail';
import SDGScoreChart, { CHART_MODES } from '../charts/sdg-score-chart';
import G17EcoLogo from '../../images/g17Eco.svg';
import './styles.scss';
import { SdgChangeFn } from './SdgCharProps';
import { isCTL } from '../../utils/permission-groups';
import { QUESTION } from '../../constants/terminology';

export const tooltips = {
  CONTRIBUTION: (
    <div className='text-left text-md'>
      <strong>Contribution:</strong> SDG Contribution calculates the effort an organisation is making through its initiatives
      across its value chain towards the successful delivery of the SDGs.
    </div>
  ),
  SCOPE: (
    <div className='text-left text-md'>
      <strong>Scope:</strong> The relevant universe of {QUESTION.PLURAL} needed to address the SDGs at the Target level. {QUESTION.CAPITALIZED_PLURAL} are only included for Targets where business can support the achievement of this target through core business strategies and operations, as defined by the UNGC and GRI.
    </div>
  ),
  MATERIALITY: (
    <div className='text-left text-md'>
      <strong>Materiality:</strong> Materially relevant SDG’s are industry-specific and establish goals that organisations
      have the ability to substantively positively and/or negatively impact.
    </div>
  )
};

export interface ChartProps {
  scorecard: ScorecardResponse,
  initiativeId: string;
  handleChangeSDG?: SdgChangeFn;
  sdgCode?: string | number;
  surveyId?: string;
  showDetail?: boolean;
  showPoweredBy?: boolean;
  toggleMaterialityBtn?: React.JSX.Element
}

export const SdgChart = React.forwardRef((props: ChartProps, ref) => {
  const { scorecard, initiativeId, sdgCode, surveyId, handleChangeSDG, showDetail = true, showPoweredBy = false } = props;
  const [chartMode] = React.useState(CHART_MODES.GOAL);
  const isCompanyTrackerLight = isCTL(scorecard.initiative);
  const scorecardState = React.useMemo(() => {
    return {
      loaded: true,
      loading: false,
      data: scorecard,
    } as ScorecardInnerLoaded
  }, [scorecard])


  return <div className='sdgChart'>
    <div ref={ref as LegacyRef<any>} className='pb-2'>
      <div className='mt-2 position-relative' style={{ minHeight: '300px' }}>
        <SDGScoreChart initiative={scorecard.initiative} goals={scorecard.scorecard.goals} mode={chartMode} handleOnClick={handleChangeSDG} />
        <div className={`net-contribution-labels ${chartMode !== CHART_MODES.TARGET ? 'd-none' : 'd-flex flex-column'}`}>
          <div className='net-contribution-labels-right flex-fill'>+ value creating</div>
          <div className='net-contribution-labels-left flex-fill'>- value eroding</div>
        </div>
      </div>
      <div className='controlStripContainer' style={{ position: 'relative', marginTop: -5 }}>
        <SDGControlStrip
          scorecardState={scorecardState}
          isCompanyTrackerLight={isCompanyTrackerLight}
          border={false}
          handleClick={handleChangeSDG}
          showKeyText={false}
          initiativeId={initiativeId}
          surveyId={surveyId}
          defaultSelectedSDGs={[sdgCode]}
          displayProgressBars={false}
        />
        <div className='strip-materiality-heading'>{props.toggleMaterialityBtn}<div><span>Materiality</span></div></div>
      </div>
      <div className={`powered-by mt-1 ${showPoweredBy ? 'd-flex' : 'd-none'} justify-content-end text-ThemeTextPlaceholder mr-2`}>
        <div>powered by</div>
        <div>
          <div className='ml-2 text-right'>
            <img src={G17EcoLogo} height={20} width={80} alt='G17Eco' />
          </div>
          <div className='powered-by-url text-right'>
            www.g17.eco
          </div>
        </div>
      </div>
    </div>
    {showDetail && sdgCode ? <div className='mt-5'>
      <SdgDetailView sdgCode={sdgCode} scorecard={scorecard} />
    </div>
      : <></>}
  </div>
});
